import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { jsonLd } from '@/components/SEO';
import FAQ, { type FAQItem } from '@/components/FAQ';
import Callout from '@/components/Callout';
import { Choice, Field, Select, Panel, ReadingList, ToolIntro, ToolSection } from '@/components/ToolKit';
import { AUTHOR, ORACLE_FACTS, SITE_NAME, SITE_ORIGIN, absoluteUrl } from '@/lib/site';

// The decision guide: four forward paths for a WebCenter AP estate, an
// eight-question diagnostic whose reading follows the guide's stated rules,
// stack-specific considerations and a vendor checklist. Client-side only.
const FIRST_PUBLISHED = '2026-06-29';
const LAST_REVIEWED = '2026-09-16';
const PATH = '/tools/webcenter-decision-guide';

import { readAnswers, UNKNOWN_ESTATE, DECISION_MODEL_VERSION, type Answer, type Estate } from '@/lib/tools/decision-guide-model';
import { downloadJson } from '@/lib/tools/download';

const ANSWERS: { value: Answer; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
];

interface Question {
  id: string;
  text: string;
}

const QUESTIONS: Question[] = [
  {
    id: 'q1',
    text: 'Is the business asking for a measurable improvement the current AP setup does not provide, such as extraction accuracy, reduced review work, supplier-email handling or usability?',
  },
  {
    id: 'q2',
    text: 'Does onboarding new suppliers regularly require Forms Recognition template authoring or tuning?',
  },
  {
    id: 'q3',
    text: 'Does working knowledge of the WebCenter estate sit with one or two people?',
  },
  {
    id: 'q4',
    text: 'Does supplier-email triage (status chasing, missing PO numbers, duplicate submissions) take a meaningful share of the AP team’s week?',
  },
  {
    id: 'q5',
    text: 'Does the ADF non-PO coding form need a development cycle for routine changes?',
  },
  {
    id: 'q6',
    text: 'Is there budget allocated for this work in the next six to twelve months?',
  },
  {
    id: 'q7',
    text: 'Are you in a regulated sector where software outside vendor support is flagged in audit scope?',
  },
  {
    id: 'q8',
    text: 'Is an E-Business Suite to Fusion Cloud migration on the wider roadmap?',
  },
];

const PATHS = [
  {
    name: 'Path A: move to OCI as-is',
    body: 'Evaluate relocating an eligible WebCenter deployment to OCI. Confirm certified software, licensing, identity, storage and integration requirements, then rehearse the move. A hosting change does not extend the installed release’s support lifecycle; it has its own delivery cost.',
    fit: 'Funded estates with a documented infrastructure reason to move and a separately agreed software support plan.',
    href: '/migrations/webcenter-oci-marketplace-vs-modernize',
    hrefLabel: 'OCI Marketplace versus re-platforming',
  },
  {
    name: 'Path A+: evaluate a supported 14c upgrade',
    body: `Evaluate the applicable 14c target (${ORACLE_FACTS.release14c}) for each component. Confirm the supported source path, compatibility, data changes, rehearsal, regression and recovery plan. Retaining an architecture does not mean upgrading the source environment in place.`,
    fit: 'Estates with significant WebCenter investment that want the longest runway without a platform change.',
    href: '/upgrades/webcenter-14c-upgrade',
    hrefLabel: 'The WebCenter 14c upgrade',
  },
  {
    name: 'Path B: re-platform the AP layer',
    body: 'Evaluate a replacement for the required capture, extraction, workflow and image-retrieval functions. Confirm documented interfaces for the actual E-Business Suite, PeopleSoft or Fusion release and test the complete transaction flow. The Oracle ERP stays the system of record. Establish scope, compatibility and schedule from the actual workload.',
    fit: 'Organizations ready for the strategic decision, using the 12c support boundary to change how AP runs, not only where it runs.',
    href: '/migrations/modernize-oracle-webcenter',
    hrefLabel: 'Re-platforming a WebCenter AP estate',
  },
  {
    name: 'Path B+: hybrid',
    body: 'Re-platform the AP layer and keep WebCenter for the content management it also does (records, document management, Portal, Sites), moving that part to OCI or 14c on its own timeline. Budget and validate the AP and retained-content tracks separately.',
    fit: 'Estates with diverse WebCenter use where AP is the most strained component.',
    href: '/migrations/webcenter-content-repository-to-oci',
    hrefLabel: 'WebCenter Content repository to OCI',
  },
];

const STACK = [
  {
    title: 'FIPSA',
    body: 'The Financials Image Processing Solution Accelerator provides reference patterns for invoice processing with E-Business Suite and PeopleSoft. Inventory the capture, recognition, Imaging/Content, workflow, ERP interfaces and customizations actually deployed. Confirm each component’s version and integration pattern; the FIPSA label alone does not establish the installed stack or upgrade compatibility.',
    href: '/migrations/modernize-webcenter-fipsa',
    hrefLabel: 'FIPSA forward paths',
  },
  {
    title: 'Forms Recognition',
    body: 'Assess the project’s actual recognition methods, learned data where configured, validation scripts and ERP business rules. Compare upgrade and replacement options against representative documents, field accuracy, exception handling and observed review effort. Confirm whether and how learning is configured; neither a new extraction engine nor routine operator corrections guarantee improvement.',
    href: '/migrations/modernize-webcenter-forms-recognition',
    hrefLabel: 'Forms Recognition options',
  },
  {
    title: 'The ADF non-PO coding form',
    body: 'Inventory coding rules, validations, roles and ERP dependencies in the form actually deployed. For an upgrade, test compatibility and behavior. For a replacement, establish which changes are configurable and which require development, then verify the same coding and approval controls.',
    href: '/migrations/adf-non-po-coding-form-modernization',
    hrefLabel: 'ADF non-PO coding form',
  },
  {
    title: 'SOA Suite composites',
    body: 'Where AP workflows use SOA composites, document approval routing, hold codes, exceptions, adapters and in-flight instances. An upgrade needs release-specific compatibility and regression checks. A replacement needs an explicit mapping of retained business rules and evidence that the new process behaves correctly.',
    href: '/upgrades/oracle-soa-suite-12c-to-14c-upgrade',
    hrefLabel: 'SOA Suite 12c to 14c',
  },
  {
    title: 'WebCenter Imaging and Managed Attachments',
    body: 'Confirm the attachment adapter and transaction-to-document mapping used by the actual EBS or PeopleSoft deployment. Fusion retrieval requires its own interface and access design. Test historical image retrieval, permissions and audit access from the ERP before cutover; relocating storage alone does not preserve these behaviors.',
    href: '/notes/webcenter-content-managed-attachments-ebs',
    hrefLabel: 'Managed Attachments with EBS',
  },
];

const CHECKLIST = [
  'Documented integration interfaces for the installed EBS, PeopleSoft or Fusion release, with validated posting, retrieval and error handling. No writes to ERP base tables.',
  'Depth across the whole WebCenter stack: Forms Recognition, SOA Suite, WebLogic, FIPSA, ADF, Imaging. Not AP automation alone.',
  'Senior engineers on discovery and design. A complex WebCenter implementation read by a junior team produces an incomplete plan.',
  'References for the same shape of work: WebCenter AP upgrades or re-platforming, not general WebCenter consulting.',
  'A defined deliverable with a fixed scope, and one you keep whether or not the implementation follows.',
  'For Path B: a replacement platform with a published reference architecture, not a generic OCR-plus-workflow product.',
  'For Path A and A+: a written test plan for customizations and integrations, and a rollback position for the cutover weekend.',
];

const ENGAGEMENT = [
  {
    title: 'Estate review',
    body: 'A fixed-scope read of the implementation: Forms Recognition projects and business rules, SOA composites, WebLogic configuration, accelerators, ADF customizations, and EBS, PeopleSoft or Fusion integrations. Output: a written inventory, constraints, options and a recommended sequence.',
  },
  {
    title: 'Upgrade window',
    body: 'The 12c to 14c upgrade delivered as a program: environment build, out-of-place upgrade, customization regression, integration testing, cutover.',
  },
  {
    title: 'Standing retainer',
    body: 'Ongoing engineering for an estate working through a multi-quarter decision with internal stakeholders, procurement and Oracle: architecture, upgrade versus re-platform trade-offs, Fusion migration sequencing.',
  },
  {
    title: 'Defined outcomes',
    body: 'Template audits, FIPSA assessments, Imaging archive migrations, 14c upgrade planning, OCI architecture and security, custom SOA and ADF integration work. Scoped to a result, delivered as documents you keep.',
  },
];

const FAQS: FAQItem[] = [
  {
    q: 'Which path is right for a WebCenter AP estate before December 2026?',
    a: 'It depends on whether the problem is infrastructure or operations. If the AP process works, assess the support entitlement and an eligible release upgrade. OCI hosting is a separate infrastructure decision and does not extend software support. If the business is asking for capability WebCenter AP does not give it, Path B re-platforms the AP layer while the Oracle ERP stays the system of record. Path B+ is Path B for AP with the rest of WebCenter kept on its own track.',
  },
  {
    q: 'What happens to Fusion Middleware 12c after December 2026?',
    a: `Premier Support for Fusion Middleware 12c ends ${ORACLE_FACTS.fmw12cPremierEnds} and Extended Support ends ${ORACLE_FACTS.fmw12cExtendedEnds}. After that the standard position is Sustaining Support: access to existing patches and documentation, no new patches or certifications. Oracle has offered Market Driven Support for selected releases; confirm the terms for 12.2.1.4 against the current Lifetime Support Policy.`,
  },
  {
    q: 'Does the diagnostic send my answers anywhere?',
    a: 'The diagnostic processes the estate details and answers locally. It does not upload or save them. Printing or downloading is an explicit action you choose.',
  },
  {
    q: 'Can Path A be a stepping stone to Path B?',
    a: 'It can be, if the relocation has an independent business case, funded scope and a valid software support plan. Compare overlapping costs and avoid assuming that a change of hosting changes the software lifecycle.',
  },
];

const WebCenterDecisionGuide = () => {
  const [answers, setAnswers] = useState<Record<string, Answer | null>>(() =>
    Object.fromEntries(QUESTIONS.map((q) => [q.id, null])),
  );
  const [estate, setEstate] = useState<Estate>(UNKNOWN_ESTATE);
  const reading = useMemo(() => readAnswers(answers, estate), [answers, estate]);
  const answered = QUESTIONS.filter((q) => answers[q.id] !== null).length;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'WebCenter 12c decision guide: the four forward paths',
    description:
      'Four forward paths for an Oracle WebCenter AP estate as Fusion Middleware 12c Premier Support ends in December 2026, with an eight-question diagnostic, stack-specific considerations and a vendor checklist.',
    datePublished: FIRST_PUBLISHED,
    dateModified: LAST_REVIEWED,
    inLanguage: 'en-US',
    url: absoluteUrl(PATH),
    mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(PATH) },
    author: { '@type': 'Person', name: AUTHOR.name, jobTitle: AUTHOR.jobTitle },
    publisher: { '@type': 'Organization', '@id': `${SITE_ORIGIN}/#organization`, name: SITE_NAME },
  };

  return (
    <>
      <Head>
        <script type="application/ld+json">{jsonLd(articleSchema)}</script>
      </Head>

      <ToolIntro>
        <p>
          Begin with the current release, hosting, retained workloads and real constraints. Then use the AP questions to
          compare possible directions. Unknown facts produce a discovery list; no allocated funding produces a scoping
          and funding step. Confirm each component’s support entitlement separately. The reading is a starting point
          for your team, not a platform recommendation or a promise of deliverability.
        </p>
      </ToolIntro>

      <ToolSection id="estate" title="Establish the current estate">
        <Panel><div className="grid gap-6 sm:grid-cols-2">
          {([
            ['version', 'Current release', [['unsure', 'Not sure'], ['11g', '11g'], ['12c', '12c'], ['14c', '14c']]],
            ['deployment', 'Current hosting', [['unsure', 'Not sure'], ['on-prem', 'On-premises'], ['oci', 'OCI'], ['hybrid', 'Hybrid']]],
            ['workloads', 'Workloads to retain', [['unsure', 'Not sure'], ['ap', 'AP only'], ['mixed', 'AP plus other WebCenter workloads'], ['non-ap', 'No AP workload']]],
            ['support', 'Current support entitlement', [['unsure', 'Not confirmed'], ['confirmed', 'Coverage and terms confirmed'], ['gap', 'Known coverage gap']]],
            ['deadline', 'Required decision or cutover deadline', [['unsure', 'Not sure'], ['near-term', 'Within six months'], ['later', 'Later than six months'], ['none', 'No fixed deadline']]],
            ['fusionDeadline', 'Mandatory Fusion cutover within six months?', [['unsure', 'Not sure'], ['mandatory-near', 'Yes'], ['none', 'No']]],
          ] as [keyof Estate, string, string[][]][]).map(([key, label, options]) => <Field key={key} htmlFor={`estate-${key}`} label={label}>
            <Select id={`estate-${key}`} value={estate[key]} onChange={e => setEstate(prev => ({ ...prev, [key]: e.target.value }))}>{options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select>
          </Field>)}
        </div></Panel>
      </ToolSection>
      <ToolSection id="diagnostic" title="Eight questions about your AP requirements">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <div className="border-t border-rule">
            {QUESTIONS.map((q, i) => (
              <Choice
                key={q.id}
                name={q.id}
                legend={
                  <>
                    <span className="label mr-3">{String(i + 1).padStart(2, '0')}</span>
                    {q.text}
                  </>
                }
                value={answers[q.id]}
                options={ANSWERS}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))}
          </div>
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Panel>
              <p className="label">Reading</p>
              {reading ? (
                <div role="status">
                  <p className="mt-3 text-[1.75rem] font-bold leading-[1.2]">{reading.path}</p>
                  <p className="mt-3 text-small">{reading.headline}</p>
                  <p className="mt-3 text-body">{reading.why}</p>
                  {reading.notes.length > 0 && (
                    <ul className="mt-5 space-y-3 border-t border-rule pt-5">
                      {reading.notes.map((n) => (
                        <li key={n} className="border-l-[3px] border-l-brass pl-4 text-body">
                          {n}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-body" role="status">
                  Answer all eight questions. {answered} of {QUESTIONS.length} answered.
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="btn-outline-forest" onClick={() => { setEstate(UNKNOWN_ESTATE); setAnswers(Object.fromEntries(QUESTIONS.map(q => [q.id, null]))); }}>Reset</button>
                {reading && <><button className="btn-outline-forest" onClick={() => window.print()}>Print</button><button className="btn-outline-forest" onClick={() => downloadJson('webcenter-decision-reading.json', { modelVersion: DECISION_MODEL_VERSION, generatedAt: new Date().toISOString(), estate, answers, reading })}>Download reading</button></>}
              </div>
              <p className="mt-4 text-[0.9375rem]">Model {DECISION_MODEL_VERSION}. Changing funding, support, retained workloads or the ERP deadline can change this reading.</p>
            </Panel>
          </div>
        </div>
      </ToolSection>

      <ToolSection id="reading" title="How to read the result">
        <ReadingList
          items={[
            {
              title: 'Questions 1, 4 and 5 are the operational test',
              body: 'Capability gaps, email triage load and the cost of changing the ADF form. With funding and constraints established, two or more “yes” answers justify evaluating Path B, or B+ for mixed workloads. They do not establish replacement performance or savings.',
            },
            {
              title: 'Funding and known estate facts are gates',
              body: 'Unknown release, hosting, workloads, entitlement, deadline or budget produces discovery first. No allocated budget produces a scope-and-fund result. A paid OCI migration is not a no-budget bridge.',
            },
            {
              title: 'Questions 7 and 8 change the timing and the leverage',
              body: 'Confirm actual audit obligations and the ERP roadmap. A mandatory near-term Fusion cutover needs a coordinated continuity plan before any platform choice; conflicting answers are flagged.',
            },
            {
              title: 'Questions 2 and 3 are carried as notes',
              body: 'Supplier churn and concentrated knowledge identify evidence to collect. Already on 14c with no established operational case means review the current baseline; it does not mean upgrade to 14c again.',
            },
          ]}
        />
      </ToolSection>

      <ToolSection id="forcing" title="The forcing function: the 12c support timeline">
        <div className="max-w-[46rem] space-y-5 text-body">
          <p>
            The Oracle Lifetime Support Policy gives family-level dates for applicable Fusion Middleware releases.
            Verify each installed product, release and entitlement. Forms Recognition has separate build and runtime
            considerations; a shared product family does not establish identical versioning or coverage.
          </p>
          <ul className="border-t border-rule">
            <li className="border-b border-rule py-4">
              <p className="text-small">{ORACLE_FACTS.fmw12cPremierEnds}: Premier Support ends</p>
              <p className="mt-1">
                The available coverage changes with the support tier. Confirm the exact product entry, policy footnotes,
                patch eligibility and certification requirements before planning a bridge.
              </p>
            </li>
            <li className="border-b border-rule py-4">
              <p className="text-small">{ORACLE_FACTS.fmw12cExtendedEnds}: Extended Support ends</p>
              <p className="mt-1">
                Extended Support scope is governed by Oracle’s policy and the applicable agreement. Check the available
                fixes, certifications and release-specific exceptions for your estate.
              </p>
            </li>
            <li className="border-b border-rule py-4">
              <p className="text-small">After 2027: Sustaining Support, or a further program if Oracle offers one</p>
              <p className="mt-1">
                Sustaining Support and any separately offered program have different coverage. Confirm the scope,
                eligibility, availability and price in writing; a future program is not an assumed entitlement.
              </p>
            </li>
          </ul>
          <p>
            Have the accountable owner establish the actual audit, support and continuity obligations. A sector label
            alone does not establish an estate’s required support tier or deadline.
          </p>
          <p>
            <Link to="/tools/oracle-fusion-middleware-support-dates" className="link">
              The full support-dates table, component by component
            </Link>
          </p>
        </div>
      </ToolSection>

      <ToolSection id="paths" title="The four forward paths">
        <ul className="grid gap-px border border-rule bg-rule md:grid-cols-2">
          {PATHS.map((p) => (
            <li key={p.name} className="bg-cream p-6 md:p-7">
              <h3 className="text-[1.375rem] font-bold leading-[1.3]">{p.name}</h3>
              <p className="mt-3 text-body">{p.body}</p>
              <p className="mt-3 text-body">
                <span className="font-bold">Best fit:</span> {p.fit}
              </p>
              <Link to={p.href} className="link mt-4 inline-block text-small">
                {p.hrefLabel}
              </Link>
            </li>
          ))}
        </ul>
      </ToolSection>

      <ToolSection id="stack" title="Stack-specific considerations">
        <ul className="max-w-[46rem] border-t border-rule">
          {STACK.map((s) => (
            <li key={s.title} className="border-b border-rule py-5">
              <h3 className="text-small">{s.title}</h3>
              <p className="mt-2 text-body">{s.body}</p>
              <Link to={s.href} className="link mt-2 inline-block text-[1rem] font-bold">
                {s.hrefLabel}
              </Link>
            </li>
          ))}
        </ul>
      </ToolSection>

      <ToolSection id="checklist" title="Vendor evaluation checklist">
        <p className="max-w-[46rem] text-body">Whichever path you choose, test any vendor for the following.</p>
        <ul className="mt-5 max-w-[46rem] border-t border-rule">
          {CHECKLIST.map((item) => (
            <li key={item} className="flex gap-4 border-b border-rule py-4 text-body">
              <span aria-hidden className="mt-2 h-3 w-3 shrink-0 bg-forest" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </ToolSection>

      <ToolSection id="engage" title="How ECMWorks engages">
        <div className="max-w-[46rem]">
          <Callout kind="note">
            <p>
              The same engineers work every shape below, whichever path the estate ends up on. Scope and price are set
              per engagement, in writing, after a scoping conversation.
            </p>
          </Callout>
          <ul className="border-t border-rule">
            {ENGAGEMENT.map((e) => (
              <li key={e.title} className="border-b border-rule py-5">
                <h3 className="text-small">{e.title}</h3>
                <p className="mt-2 text-body">{e.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-body">
            <Link to="/support" className="link">
              Support retainers and the upgrade window
            </Link>{' '}
            ·{' '}
            <Link to="/migrations" className="link">
              Migration guides
            </Link>
          </p>
        </div>
      </ToolSection>

      <div className="mt-16 max-w-[46rem]">
        <FAQ items={FAQS} />
      </div>
    </>
  );
};

export default WebCenterDecisionGuide;
